import PageHeader from './components/PageHeader';
import { SummaryReportSection, SummaryReportTable } from './components/SummaryReport';
import RetailIdQrScanner from './components/RetailIdQrScanner';
import type { ScanRegulatoryReportRow } from './hooks/useScanRegulatoryReport';
import { formatDate as formatCalendarDate } from './utils/formatDate';

function formatTimestamp(value?: string | null) {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function Details({ title, testId, rows }: { title: string; testId: string; rows: [string, string | number | undefined | null][] }) {
  return (
    <SummaryReportSection title={title} testId={testId}>
      <SummaryReportTable rows={rows.map(([label, value]): [string, unknown] => [
        label,
        value === undefined || value === null || value === '' ? 'Not recorded' : value,
      ])} />
    </SummaryReportSection>
  );
}

type Props = {
  allowed: boolean; qr: string; scanning: boolean; loading: boolean; error: string;
  rows: ScanRegulatoryReportRow[];
  onQrChange: (value: string) => void; onLookup: () => void; onToggleCamera: () => void;
  onScan: (value: string) => void; onCameraError: (message: string) => void; onBack: () => void;
};

export default function ScanRegulatoryReportPage({ allowed, qr, scanning, loading, error, rows,
  onQrChange, onLookup, onToggleCamera, onScan, onCameraError, onBack }: Props) {
  return (
    <div className="stack" data-testid="page-scan-regulatory-report">
      <PageHeader
        title="Scan Regulatory Report"
        subtitle="Look up an issued QR using information already stored in RID"
        actions={<button className="button" type="button" onClick={onBack}>Back</button>}
      />
      {!allowed ? (
        <div className="card" style={{ padding: '2rem' }}>You do not have permission to view this report.</div>
      ) : (
        <>
          <div className="card stack" style={{ padding: '2rem' }}>
            <form onSubmit={(event) => { event.preventDefault(); onLookup(); }} className="stack">
              <label htmlFor="retail-id-qr" style={{ fontWeight: 600 }}>QR URL or Retail ID short code</label>
              <input
                id="retail-id-qr"
                className="input"
                value={qr}
                onChange={(event) => onQrChange(event.target.value)}
                maxLength={2048}
                autoComplete="off"
                placeholder="Scan or paste a Retail ID QR URL"
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button className="button cta" type="submit">Look up</button>
                <button
                  className="button"
                  type="button"
                  onClick={onToggleCamera}
                >
                  {scanning ? 'Stop camera' : 'Scan QR with camera'}
                </button>
              </div>
            </form>
            {scanning && <RetailIdQrScanner onScan={onScan} onError={onCameraError} />}
            <p className="muted">
              Location means the recorded package facility and market, not the scanner&apos;s current location.
              Event timestamps use your local time; packaged dates retain their calendar day. Missing information is shown as Not recorded.
            </p>
          </div>
          {loading && <p role="status">Loading Scan Regulatory Report...</p>}
          {error && <div className="card" role="alert" style={{ padding: '1rem' }}>{error}</div>}
          <div className="stack" aria-live="polite" aria-busy={loading}>
            {rows.map((row) => (
              <article className="card" style={{ padding: '1.5rem' }} key={row.printRunId} data-testid="scan-regulatory-report-result">
                <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1rem', fontFamily: 'monospace', letterSpacing: '0.02em', overflowWrap: 'anywhere' }}>
                  Print run {row.printRunId}{row.jobName ? `: ${row.jobName}` : ''}
                </h2>
                  <Details title="Package" testId={`scan-regulatory-${row.printRunId}-package`} rows={[
                    ['Package label', row.packageLabel], ['Package type', row.packageType],
                    ['Production batch', row.productionBatchNumber], ['Retail ID', row.retailId],
                    ['Label index', row.labelIndex],
                  ]} />
                  <Details title="Product" testId={`scan-regulatory-${row.printRunId}-product`} rows={[
                    ['Product name', row.productName], ['Category', row.productCategory], ['Strain', row.strain],
                  ]} />
                  <Details title="Licensee and location" testId={`scan-regulatory-${row.printRunId}-licensee-location`} rows={[
                    ['Organization', row.organizationName], ['Facility', row.facilityName],
                    ['License', row.license], ['Market', row.marketName],
                  ]} />
                  <Details title="Package and print dates" testId={`scan-regulatory-${row.printRunId}-dates`} rows={[
                    ['Package packaged date', formatCalendarDate(row.packagedDate) || 'Not recorded'],
                    ['Print-run packaged date', formatCalendarDate(row.printPackagedDate) || 'Not recorded'],
                    ['Labels last generated', formatTimestamp(row.labelsLastGeneratedDateTime)],
                    ['Print run created', formatTimestamp(row.printCreatedAt)],
                    ['Printed date', formatTimestamp(row.printedDate)],
                  ]} />
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
